import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { Logo } from "@/components/Logo";
import { ShaderBackground } from "@/components/ui/hero";
import { sendTransactionalEmail } from "@/lib/email/send";
import { claimReferral } from "@/lib/referrals.functions";

export const Route = createFileRoute("/auth")({
  head: () => ({ meta: [{ title: "Entrar — Viny Club" }] }),
  validateSearch: (s: Record<string, unknown>): { next?: string } =>
    typeof s.next === "string" ? { next: s.next } : {},

  component: Auth,
});

/** Only same-origin relative paths are allowed as post-login redirects. */
function safeNext(next?: string): string | null {
  if (!next || !next.startsWith("/") || next.startsWith("//")) return null;
  return next;
}


function authErrorMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : "";
  const code = (err as { code?: string })?.code ?? "";
  const m = raw.toLowerCase();
  if (code === "weak_password" || m.includes("weak") || m.includes("pwned"))
    return "Essa senha é muito comum e foi vazada em outros sites. Escolha uma senha mais forte (misture letras, números e símbolos).";
  if (m.includes("already registered") || m.includes("already been registered") || code === "user_already_exists")
    return "Este email já tem conta. Faça login ou use “Esqueci minha senha”.";
  if (m.includes("invalid login credentials")) return "Email ou senha incorretos.";
  if (m.includes("email not confirmed")) return "Confirme seu email antes de entrar.";
  if (m.includes("password should be at least") || m.includes("at least 6"))
    return "A senha precisa ter pelo menos 6 caracteres.";
  if (m.includes("rate limit") || m.includes("too many"))
    return "Muitas tentativas. Aguarde alguns minutos e tente novamente.";
  if (m.includes("invalid email") || m.includes("unable to validate email"))
    return "Email inválido. Verifique e tente novamente.";
  if (m.includes("duplicate key") || m.includes("profiles_username"))
    return "Esse username já está em uso. Escolha outro.";
  if (m.includes("database error")) return "Não foi possível criar sua conta agora. Tente outro username ou tente de novo em instantes.";
  return raw || "Erro ao autenticar";
}

function Auth() {
  const navigate = useNavigate();
  const { next } = Route.useSearch();
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("discogs") === "error") {
      toast.error("O Discogs está ocupado agora. Aguarde alguns segundos e tente de novo.");
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);
  const nextPath = safeNext(next);
  const afterAuthUrl = `${typeof window !== "undefined" ? window.location.origin : ""}${nextPath ?? "/explore"}`;
  const goAfterAuth = (isNew = false) => {
    if (isNew) navigate({ to: "/onboarding" });
    else if (nextPath) window.location.href = nextPath;
    else navigate({ to: "/explore" });
  };

  const [mode, setMode] = useState<"login" | "signup" | "forgot">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  async function handleEmail(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        const cleanUsername = username.toLowerCase().replace(/[^a-z0-9_]/g, "");
        if (cleanUsername.length < 3) {
          toast.error("Username precisa ter ao menos 3 letras/números");
          return;
        }
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: afterAuthUrl,
            data: { username: cleanUsername },
          },
        });
        if (error) throw error;
        // Envia welcome em background (não bloqueia)
        sendTransactionalEmail({
          templateName: "welcome",
          recipientEmail: email,
          idempotencyKey: `welcome-${cleanUsername}-${Date.now()}`,
          templateData: {
            name: cleanUsername,
            username: cleanUsername,
            bioLink: `https://viny.club/${cleanUsername}`,
          },
        }).catch(() => {});
        // Claim referral if landing page stored one
        try {
          const ref = localStorage.getItem("viny_ref");
          if (ref) {
            await claimReferral({ data: { ref } }).catch(() => {});
            localStorage.removeItem("viny_ref");
          }
        } catch {}
        // Se já veio sessão (auto-confirm ativo), entra direto
        if (data.session) {
          toast.success("Conta criada! Bem-vindo.");
          goAfterAuth(true);
        } else {
          // Fallback: tenta autenticar com as credenciais recém-criadas
          const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
          if (signInErr) {
            toast.success("Conta criada! Confirme seu email para entrar.");
            setMode("login");
          } else {
            toast.success("Conta criada! Bem-vindo.");
            goAfterAuth(true);
          }
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Bem-vindo de volta!");
        goAfterAuth();
      }
    } catch (err: unknown) {
      toast.error(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setGoogleLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: afterAuthUrl,
      });
      if (result.error) {
        toast.error("Erro no login com Google");
        return;
      }
      if (result.redirected) return;
      goAfterAuth();
    } finally {
      setGoogleLoading(false);
    }
  }

  async function handleForgot(e: React.FormEvent) {
    e.preventDefault();
    if (!email) {
      toast.error("Digite seu email");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setForgotSent(true);
      toast.success("Email enviado! Verifique sua caixa de entrada.");
    } catch (err: unknown) {
      toast.error(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="grid min-h-screen bg-black text-white lg:grid-cols-2">
      {/* Editorial side */}
      <div className="relative hidden overflow-hidden lg:block">
        <ShaderBackground className="absolute inset-0" />
        <div className="absolute inset-0 [background:radial-gradient(circle_at_30%_30%,rgba(0,0,0,0.2),rgba(0,0,0,0.85)_70%)]" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12">
          <Logo variant="light" size={32} />
        </div>
      </div>

      {/* Form side — floating white card */}
      <div className="relative flex items-center justify-center p-4 lg:p-6">
        <div className="flex w-full max-w-md flex-col justify-center rounded-[28px] bg-white p-8 text-neutral-900 shadow-2xl shadow-black/30 lg:max-h-[calc(100vh-40px)] lg:overflow-auto lg:p-10">
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <Logo variant="dark" size={40} />
          </div>

          <Link
            to="/"
            className="inline-flex w-fit items-center gap-2 rounded-full px-3 py-1.5 text-sm text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
          >
            <ArrowLeft className="h-4 w-4" /> Voltar
          </Link>
          <h1 className="mt-5 font-display text-4xl font-medium leading-[1] tracking-[-0.03em] text-neutral-950">
            {mode === "login" ? (
              <>
                Entrar no{" "}
                <em className="font-serif italic font-normal text-neutral-500">Viny Club</em>
              </>
            ) : mode === "signup" ? (
              <>
                Criar sua{" "}
                <em className="font-serif italic font-normal text-neutral-500">conta</em>
              </>
            ) : (
              <>
                Redefinir{" "}
                <em className="font-serif italic font-normal text-neutral-500">senha</em>
              </>
            )}
          </h1>
          <p className="mt-3 text-sm text-neutral-500">
            {mode === "login"
              ? "Continue sua jornada pelo mundo dos bolachões."
              : mode === "signup"
                ? "Comece a catalogar sua coleção em segundos."
                : "Enviamos um link para redefinir sua senha."}
          </p>

          {mode !== "forgot" && (
            <>
              <button
                type="button"
                onClick={handleGoogle}
                disabled={googleLoading}
                className="mt-8 flex w-full items-center justify-center gap-3 rounded-full border border-neutral-200 bg-white px-4 py-3 text-sm font-medium text-neutral-800 shadow-sm transition-colors hover:bg-neutral-50 disabled:opacity-60"
              >
                {googleLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <svg className="h-[18px] w-[18px] shrink-0" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                  </svg>
                )}
                Continuar com Google
              </button>
              <a
                href="/api/public/discogs/login"
                className="mt-3 flex w-full items-center justify-center gap-3 rounded-full bg-neutral-950 px-4 py-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-neutral-800"
              >
                <span className="grid h-[18px] w-[18px] place-items-center rounded-full border-[3px] border-white/90">
                  <span className="h-1 w-1 rounded-full bg-white" />
                </span>
                Continuar com Discogs
              </a>

              <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-neutral-400">
                <div className="h-px flex-1 bg-neutral-200" />
                ou
                <div className="h-px flex-1 bg-neutral-200" />
              </div>
            </>
          )}

          <form className="space-y-3" onSubmit={mode === "forgot" ? handleForgot : handleEmail}>
            {mode === "signup" && (
              <input
                type="text"
                placeholder="Username (ex: vinyljunkie)"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                minLength={3}
                className="h-12 w-full rounded-full border border-neutral-200 bg-neutral-50 px-5 text-sm text-neutral-900 placeholder-neutral-400 transition-colors focus:border-neutral-400 focus:bg-white focus:outline-none"
              />
            )}
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-12 w-full rounded-full border border-neutral-200 bg-neutral-50 px-5 text-sm text-neutral-900 placeholder-neutral-400 transition-colors focus:border-neutral-400 focus:bg-white focus:outline-none"
            />
            {mode !== "forgot" && (
              <input
                type="password"
                placeholder="Senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="h-12 w-full rounded-full border border-neutral-200 bg-neutral-50 px-5 text-sm text-neutral-900 placeholder-neutral-400 transition-colors focus:border-neutral-400 focus:bg-white focus:outline-none"
              />
            )}
            <button
              type="submit"
              disabled={loading}
              className="flex h-12 w-full items-center justify-center rounded-full bg-neutral-950 text-sm font-bold text-white transition-transform hover:scale-[1.02] disabled:opacity-60"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : mode === "login" ? (
                "Entrar"
              ) : mode === "signup" ? (
                "Criar conta grátis"
              ) : (
                "Enviar link de redefinição"
              )}
            </button>
          </form>

          {mode === "login" && (
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={() => setMode("forgot")}
                className="text-xs text-neutral-500 transition-colors hover:text-neutral-900"
              >
                Esqueci minha senha
              </button>
            </div>
          )}

          <p className="mt-6 text-center text-sm text-neutral-500">
            {mode === "login"
              ? "Novo por aqui? "
              : mode === "signup"
                ? "Já tem conta? "
                : "Lembrou sua senha? "}
            <button
              type="button"
              onClick={() =>
                setMode(mode === "login" ? "signup" : "login")
              }
              className="font-semibold text-neutral-900 hover:underline"
            >
              {mode === "login"
                ? "Criar conta"
                : mode === "signup"
                  ? "Entrar"
                  : "Entrar"}
            </button>
          </p>

        </div>
      </div>
    </div>
  );
}
